import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import {
  BiSave,
  BiX,
  BiUser,
  BiBriefcase,
  BiFile,
  BiMap,
  BiPlus,
  BiTrash,
  BiDownload,
} from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js";
import axiosClient from "../utils/apiClient.js";
import Select from "react-select";
import { toast } from "react-toastify";
import { useOnboarding } from "../Onboarding/OnboardingContext";

// --- Static Data Definitions based on SQL ENUMs and Form Fields ---
const COURT_JURISDICTIONS = [
  "Circuit",
  "Family",
  "Magistrate",
  "Federal",
  "Other",
];
const SERVICE_TYPES = ["Standard", "Rush", "Same-Day", "Stakeout"];
const PREFERRED_CONTACT_METHODS = ["Email", "Phone", "Portal Notification"];
const DOCUMENT_TYPES_TO_BE_SERVED = [
  "Summons",
  "Complaint",
  "Subpoena",
  "Order",
  "Writ of Garnishment",
  "Other",
];

// Define allowed file types for servee attachments and case files
const ALLOWED_SERVE_FILE_TYPES =
  ".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp3,.mp4,.mov,.avi,.wav,.xls,.xlsx,.csv";

// --- Component Helper ---
const FormSection = ({ title, icon, children, className = "" }) => (
  <div
    className={`border border-gray-200 rounded-xl p-6 bg-white ${className}`}
  >
    <h2 className="text-xl font-extrabold text-blue-800 mb-4 flex items-center space-x-2 border-b pb-2">
      {icon}
      <span>{title}</span>
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{children}</div>
  </div>
);

// ----------------------------------------------------------------------
// Servee File Management Block
// ----------------------------------------------------------------------
const ServeeFileUploader = ({
  servee,
  index,
  handleServeeAddFile,
  handleServeeRemoveFile,
  isServeeNew,
}) => {
  const [currentServeeFileUpload, setCurrentServeeFileUpload] = useState({
    file: null,
    title: "",
  });

  const handleFileUploadTemp = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCurrentServeeFileUpload((prev) => ({
        ...prev,
        file: file,
        title: prev.title || file.name,
      }));
    }
  };

  const handleTitleChangeTemp = (e) => {
    setCurrentServeeFileUpload((prev) => ({ ...prev, title: e.target.value }));
  };

  const addFile = () => {
    if (
      currentServeeFileUpload.file &&
      currentServeeFileUpload.title.trim() !== ""
    ) {
      handleServeeAddFile(index, {
        file: currentServeeFileUpload.file,
        title: currentServeeFileUpload.title,
        isNew: true,
      });
      setCurrentServeeFileUpload({ file: null, title: "" });
      document.getElementById(`servee_file_upload_${index}`).value = null;
    } else {
      alert("Please select a file and enter a title for the servee document.");
    }
  };

  const canAddFile = servee.id || isServeeNew;

  return (
    <div className="space-y-4 border border-blue-100 p-4 rounded-lg bg-white mt-4 tour-intake-upload">
      <h4 className="text-md font-bold text-gray-700">
        Servee Attachments (Proof, Photos, etc.)
      </h4>

      {servee.servee_files && servee.servee_files.length > 0 && (
        <div className="col-span-full pt-2">
          <label className="block text-xs font-bold text-gray-700 mb-1">
            Files for {servee.full_name || `Servee ${index + 1}`} (
            {servee.servee_files.length}):
          </label>
          <ul className="text-xs text-gray-700 space-y-1">
            {servee.servee_files.map((item, fileIndex) => (
              <li
                key={item.id || fileIndex}
                className="flex justify-between items-center bg-gray-100 p-2 rounded-md border border-gray-200"
              >
                <div className="truncate pr-4 flex items-center">
                  <span
                    className={`font-semibold ${item.isNew ? "text-green-700" : "text-blue-800"}`}
                  >
                    {item.title}
                  </span>
                  <span className="text-gray-500 ml-2">
                    ({item.file_name || item.file?.name})
                  </span>
                  <span
                    className={`ml-2 text-xs font-bold ${item.isNew ? "text-green-500" : "text-gray-500"}`}
                  >
                    {item.isNew ? " (NEW - Upload Pending)" : " (Existing)"}
                  </span>
                  {!item.isNew && (
                    <a
                      href={`${API_BASE_URL}/files/download?path=${item.file_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700 ml-3"
                    >
                      <BiDownload className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    handleServeeRemoveFile(index, fileIndex, item.id)
                  }
                  className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                >
                  <BiTrash className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {canAddFile ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4 border-gray-200">
          <div className="md:col-span-2">
            <label
              htmlFor={`servee_file_title_${index}`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Attachment Title
            </label>
            <input
              type="text"
              id={`servee_file_title_${index}`}
              name="servee_file_title"
              placeholder="e.g., Entrance Photo, Proof of Service Image"
              value={currentServeeFileUpload.title}
              onChange={handleTitleChangeTemp}
              className="p-1.5 border rounded-md w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              File Upload
            </label>
            <input
              type="file"
              id={`servee_file_upload_${index}`}
              onChange={handleFileUploadTemp}
              accept={ALLOWED_SERVE_FILE_TYPES}
              className="block w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 cursor-pointer p-1.5"
            />
          </div>
          <div className="md:col-span-3">
            <button
              type="button"
              onClick={addFile}
              disabled={
                !currentServeeFileUpload.file ||
                currentServeeFileUpload.title.trim() === ""
              }
              className="flex items-center px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 transition duration-300 disabled:opacity-50"
            >
              <BiPlus className="w-4 h-4 mr-1" /> Add Servee File
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-yellow-600 mt-2">
          Note: Servee files can only be added once the case has been created
          and the Servee data is saved.
        </p>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------
// Servee Form Block
// ----------------------------------------------------------------------
const ServeeFormBlock = ({
  servee,
  index,
  handleServeeChange,
  handleRemoveServee,
  canRemove,
  handleServeeAddFile,
  handleServeeRemoveFile,
  isServeeNew,
}) => {
  return (
    <div className="col-span-full border border-gray-300 p-4 rounded-lg space-y-4 bg-gray-50 tour-intake-servee-info">
      <h3 className="text-lg font-bold text-gray-700">Servee #{index + 1}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor={`full_name_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Name of Person/Entity *
          </label>
          <input
            type="text"
            id={`full_name_${index}`}
            name="full_name"
            value={servee.full_name || ""}
            onChange={(e) => handleServeeChange(index, e)}
            required
            className="p-1.5 border rounded-md w-full"
          />
        </div>
        <div>
          <label
            htmlFor={`aliases_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Aliases / AKA
          </label>
          <input
            type="text"
            id={`aliases_${index}`}
            name="aliases"
            value={servee.aliases || ""}
            onChange={(e) => handleServeeChange(index, e)}
            className="p-1.5 border rounded-md w-full"
          />
        </div>
        <div className="col-span-full">
          <label
            htmlFor={`primary_address_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Primary Address *
          </label>
          <input
            type="text"
            id={`primary_address_${index}`}
            name="primary_address"
            value={servee.primary_address || ""}
            onChange={(e) => handleServeeChange(index, e)}
            required
            className="p-1.5 border rounded-md w-full"
          />
        </div>
        <div>
          <label
            htmlFor={`apartment_unit_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Apartment / Unit #
          </label>
          <input
            type="text"
            id={`apartment_unit_${index}`}
            name="apartment_unit"
            value={servee.apartment_unit || ""}
            onChange={(e) => handleServeeChange(index, e)}
            className="p-1.5 border rounded-md w-full"
          />
        </div>
        <div>
          <label
            htmlFor={`city_state_zip_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            City / State / ZIP
          </label>
          <input
            type="text"
            id={`city_state_zip_${index}`}
            name="city_state_zip"
            value={servee.city_state_zip || ""}
            onChange={(e) => handleServeeChange(index, e)}
            className="p-1.5 border rounded-md w-full"
          />
        </div>
        <div>
          <label
            htmlFor={`phone_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Phone (if known)
          </label>
          <input
            type="tel"
            id={`phone_${index}`}
            name="phone"
            value={servee.phone || ""}
            onChange={(e) => handleServeeChange(index, e)}
            className="p-1.5 border rounded-md w-full"
          />
        </div>
        <div>
          <label
            htmlFor={`email_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email (if known)
          </label>
          <input
            type="email"
            id={`email_${index}`}
            name="email"
            value={servee.email || ""}
            onChange={(e) => handleServeeChange(index, e)}
            className="p-1.5 border rounded-md w-full"
          />
        </div>
        <div className="col-span-full">
          <label
            htmlFor={`alternate_address_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Alternate Address (Work, Secondary, etc.)
          </label>
          <textarea
            id={`alternate_address_${index}`}
            name="alternate_address"
            value={servee.alternate_address || ""}
            onChange={(e) => handleServeeChange(index, e)}
            rows="2"
            className="p-1.5 border rounded-md w-full"
          />
        </div>
        <div className="col-span-full">
          <label
            htmlFor={`physical_description_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Physical Description
          </label>
          <textarea
            id={`physical_description_${index}`}
            name="physical_description"
            placeholder="Gender, Race, Height, Hair, Tattoos, etc."
            value={servee.physical_description || ""}
            onChange={(e) => handleServeeChange(index, e)}
            rows="3"
            className="p-1.5 border rounded-md w-full"
          />
        </div>
        <div className="col-span-full">
          <label
            htmlFor={`vehicle_description_${index}`}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Vehicle Description
          </label>
          <input
            type="text"
            id={`vehicle_description_${index}`}
            name="vehicle_description"
            placeholder="Make/Model/Tag"
            value={servee.vehicle_description || ""}
            onChange={(e) => handleServeeChange(index, e)}
            className="p-1.5 border rounded-md w-full"
          />
        </div>
      </div>

      <ServeeFileUploader
        servee={servee}
        index={index}
        handleServeeAddFile={handleServeeAddFile}
        handleServeeRemoveFile={handleServeeRemoveFile}
        isServeeNew={isServeeNew}
      />

      {canRemove && (
        <button
          type="button"
          onClick={() => handleRemoveServee(index)}
          className="flex items-center text-red-500 hover:text-red-700 text-sm mt-4"
        >
          <BiTrash className="w-4 h-4 mr-1" /> Remove Servee
        </button>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------
// Component for Displaying Existing Case Files
// ----------------------------------------------------------------------
const ExistingCaseFiles = ({ files, handleRemoveExistingFile }) => {
  const existingFiles = files.filter((f) => !f.isNew);

  if (existingFiles.length === 0) return null;

  return (
    <div className="col-span-full pt-4">
      <label className="block text-sm font-bold text-gray-700 mb-2">
        Existing Case Files ({existingFiles.length}):
      </label>
      <ul className="text-sm text-gray-700 space-y-2">
        {existingFiles.map((item) => {
          const deleteType =
            item.type ||
            (item.file_type?.includes("image") ||
            item.file_type?.includes("video") ||
            item.file_type?.includes("audio")
              ? "media"
              : "document");

          return (
            <li
              key={item.id}
              className="flex justify-between items-center bg-gray-50 p-2 rounded-md border border-gray-200"
            >
              <div className="truncate pr-4 flex items-center">
                <span className="font-semibold text-blue-800 mr-2">
                  {item.title}
                </span>
                <span className="text-gray-500 text-xs">
                  ({item.file_name} - {deleteType.toUpperCase()})
                </span>
                <a
                  href={`${API_BASE_URL}/files/download?path=${item.file_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-700 ml-3"
                >
                  <BiDownload className="w-4 h-4" />
                </a>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveExistingFile(item.id, deleteType)}
                className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
              >
                <BiTrash className="w-5 h-5" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ----------------------------------------------------------------------
// MAIN COMPONENT: CaseForm
// ----------------------------------------------------------------------
const CaseForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { search } = useLocation();
  const { runTour, stepIndex, setStepIndex } = useOnboarding();
  const PS_API_URL = `${API_BASE_URL}/cases`;

  const queryClientId = useMemo(
    () => new URLSearchParams(search).get("clientId"),
    [search],
  );

  const newServeeTemplate = {
    full_name: "",
    aliases: "",
    primary_address: "",
    apartment_unit: "",
    city_state_zip: "",
    alternate_address: "",
    phone: "",
    email: "",
    physical_description: "",
    vehicle_description: "",
    servee_files: [],
  };

  const [formData, setFormData] = useState({
    case_number: "",
    case_name: "",
    case_type: "Process Service",
    status: "Open",
    client_id: "",
    description: "",
    assigned_to_ids: [],
    due_date: "",
    county_or_region: "",
    state_or_province: "",
    court_jurisdiction: "",
    service_type: "",
    client_reference_number: "",
    number_of_pages: 0,
    client_contact_person: "",
    client_phone_number: "",
    client_email_address: "",
    client_billing_address: "",
    preferred_contact_method: "Email",
    documents_to_be_served: [],
    service_instructions: "",
    special_instructions: "",
    custom_fields: {},
  });

  const [serveeData, setServeeData] = useState([newServeeTemplate]);
  const [caseFiles, setCaseFiles] = useState([]);
  const [currentFileUpload, setCurrentFileUpload] = useState({
    file: null,
    title: "",
  });
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitStatus, setSubmitStatus] = useState("");
  const [isSaved, setIsSaved] = useState(false);

  const caseStatuses = ["Open", "In Progress", "Pending", "Closed"];

  const formatRoleName = (roleName = "") => {
    return roleName
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const token = localStorage.getItem("token");
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // --- API & Deletion Handlers ---
  const handleDeleteCaseFile = async (fileId, fileType) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this ${fileType} file? This action is permanent.`,
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(
        `${PS_API_URL}/files/${fileType}/${fileId}`,
        authHeaders,
      );
      setCaseFiles((prev) => prev.filter((f) => f.id !== fileId));
      alert(`${fileType.toUpperCase()} deleted successfully!`);
    } catch (err) {
      console.error("Error deleting general file:", err);
      alert("Failed to delete general file. Check console for details.");
    }
  };

  const handleDeleteServeeFile = async (fileId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this servee attachment? This action is permanent.",
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${PS_API_URL}/servee-files/${fileId}`, authHeaders);
      setServeeData((prev) =>
        prev.map((servee) => ({
          ...servee,
          servee_files: servee.servee_files.filter((f) => f.id !== fileId),
        })),
      );
      alert("Servee Attachment deleted successfully!");
    } catch (err) {
      console.error("Error deleting servee file:", err);
      alert("Failed to delete servee attachment. Check console for details.");
    }
  };

  // --- Auto-fill logic for Tour ---
  // useEffect(() => {
  //   if (runTour && !isEditMode && stepIndex === 4) {
  //     setFormData((prev) => ({
  //       ...prev,
  //       case_name: "Doe vs. Smith - Subpoena",
  //       county_or_region: "Springfield",
  //       state_or_province: "IL",
  //       court_jurisdiction: "Circuit",
  //       service_type: "Standard",
  //       client_reference_number: "REF-TOUR-123",
  //     }));
  //     setServeeData([
  //       {
  //         ...newServeeTemplate,
  //         full_name: "John Smith",
  //         primary_address: "123 Legal Way, Suite 400",
  //         city_state_zip: "Springfield, IL 62704",
  //       },
  //     ]);
  //   } else if (queryClientId && !formData.client_id) {
  //     setFormData((prev) => ({
  //       ...prev,
  //       client_id: parseInt(queryClientId, 10),
  //     }));
  //   }
  // }, [runTour, isEditMode, stepIndex, queryClientId, clients]);
  // --- Auto-fill logic for Tour & Standard URL Parameters ---
  useEffect(() => {
    // Prevent execution if clients haven't loaded yet
    if (clients.length === 0) return;

    // 1. SAFELY parse the ID to prevent NaN errors
    let parsedClientId = queryClientId ? parseInt(queryClientId, 10) : "";
    if (isNaN(parsedClientId)) {
      parsedClientId = "";
    }

    if (runTour && !isEditMode && stepIndex === 4 && !formData.client_id) {
      // TOUR FALLBACK: If API didn't return a valid ID, smartly pick the newest client in the list
      if (parsedClientId === "" && clients.length > 0) {
        parsedClientId = clients[clients.length - 1].id;
      }

      const selectedClient = clients.find((c) => c.id === parsedClientId) || clients[clients.length - 1];

      setFormData((prev) => ({
        ...prev,
        client_id: selectedClient ? selectedClient.id : "",
        client_contact_person: selectedClient ? (selectedClient.contact_person || `${selectedClient.first_name} ${selectedClient.last_name}`) : "",
        client_phone_number: selectedClient?.phone_number || "",
        client_email_address: selectedClient?.email || "",
        client_billing_address: selectedClient?.billing_address || "",
        preferred_contact_method: selectedClient?.preferred_contact_method || "Email",
        case_name: "Doe vs. Smith - Subpoena",
        county_or_region: "Springfield",
        state_or_province: "IL",
        court_jurisdiction: "Circuit",
        service_type: "Standard",
        client_reference_number: "REF-TOUR-123",
      }));
      setServeeData([
        {
          ...newServeeTemplate,
          full_name: "John Smith",
          primary_address: "123 Legal Way, Suite 400",
          city_state_zip: "Springfield, IL 62704",
        },
      ]);
    } else if (queryClientId && !formData.client_id) {
      // STANDARD LOGIC (Non-tour routing)
      if (parsedClientId !== "") {
        const selectedClient = clients.find((c) => c.id === parsedClientId);
        if (selectedClient) {
          setFormData((prev) => ({
            ...prev,
            client_id: parsedClientId,
            client_contact_person: selectedClient.contact_person || `${selectedClient.first_name} ${selectedClient.last_name}`,
            client_phone_number: selectedClient.phone_number || "",
            client_email_address: selectedClient.email || "",
            client_billing_address: selectedClient.billing_address || "",
            preferred_contact_method: selectedClient.preferred_contact_method || "Email",
          }));
        } else {
          setFormData((prev) => ({ ...prev, client_id: parsedClientId }));
        }
      }
    }
  }, [runTour, isEditMode, stepIndex, queryClientId, clients]);

  useEffect(() => {
    const fetchCaseLimitStatus = async () => {
      try {
        const { data } = await axiosClient.get("/subs/case-limit");
        if (data.subscriptionExpired) {
          alert(
            "Your subscription has expired. Please renew to create, edit, or assign agents to cases.",
          );
          return navigate("/cases");
        }
        if (!data.canCreateMoreCases && !isEditMode) {
          alert(
            "Your plan case limit is reached.\nDelete any case to create a new one.",
          );
          setTimeout(() => {
            navigate("/cases");
          }, 100);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchCaseLimitStatus();
  }, [navigate, isEditMode]);

  // --- Data Fetching ---
  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError("Authentication token not found.");
        setLoading(false);
        return;
      }

      try {
        const [usersRes, clientsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/users/assignable/servers`, authHeaders),
          axios.get(`${API_BASE_URL}/clients/all`, authHeaders),
        ]);
        setUsers(usersRes.data);
        setClients(
          Array.isArray(clientsRes.data?.data) ? clientsRes.data.data : [],
        );

        if (isEditMode) {
          const caseRes = await axios.get(`${PS_API_URL}/${id}`, authHeaders);
          const data = caseRes.data;

          // --- Case Files & Media (Existing) ---
          const existingDocs = (data.documents || []).map((d) => ({
            ...d,
            isNew: false,
            type: "document",
            title: d.title || d.file_name,
          }));
          const existingMedia = (data.media || []).map((m) => ({
            ...m,
            isNew: false,
            type: "media",
            title: m.title || m.file_name,
          }));
          setCaseFiles([...existingDocs, ...existingMedia]);

          // --- Servee Data with Attachments ---
          let serveesWithFiles = (data.servees || []).map((s) => {
            const existingServeeFiles = (s.servee_attachments || []).map(
              (f) => ({
                ...f,
                isNew: false,
                file_name: f.file_name,
                title: f.title,
              }),
            );

            return {
              ...s,
              servee_files: existingServeeFiles,
            };
          });

          setServeeData(
            serveesWithFiles.length > 0
              ? serveesWithFiles
              : [newServeeTemplate],
          );

          // --- Handle Assigned Agents (Multiple Select) ---
          let assignedIds = [];
          if (Array.isArray(data.assigned_to_ids) && data.assigned_to_ids.length > 0) {
            assignedIds = data.assigned_to_ids.map((idStr) => parseInt(idStr, 10)).filter((id) => !isNaN(id));
          } else if (data.assigned_to_id) {
            if (typeof data.assigned_to_id === "string") {
              assignedIds = data.assigned_to_id
                .split(",")
                .map((idStr) => parseInt(idStr.trim(), 10))
                .filter((id) => !isNaN(id));
            } else if (typeof data.assigned_to_id === "number") {
              assignedIds = [data.assigned_to_id];
            } else if (Array.isArray(data.assigned_to_id)) {
              assignedIds = data.assigned_to_id;
            }
          }

          // Documents to be served
          let docsToBeServed = [];
          if (Array.isArray(data.documents_to_be_served)) {
            docsToBeServed = data.documents_to_be_served;
          } else if (typeof data.document_served === "string" && data.document_served.trim()) {
            docsToBeServed = data.document_served.split(",").map((s) => s.trim()).filter(Boolean);
          } else if (typeof data.documents_to_be_served === "string" && data.documents_to_be_served.trim()) {
            docsToBeServed = data.documents_to_be_served.split(",").map((s) => s.trim()).filter(Boolean);
          } else if (data.documents?.length > 0 && data.documents[0].documents_to_be_served) {
            docsToBeServed = data.documents[0].documents_to_be_served.split(",").map((s) => s.trim()).filter(Boolean);
          }

          const serviceInst =
            data.service_instructions ||
            (data.documents?.length > 0
              ? data.documents.find((d) => d.service_instructions)?.service_instructions || ""
              : "") ||
            "";

          const specialInst =
            data.special_instructions ||
            (data.documents?.length > 0
              ? data.documents.find((d) => d.special_instructions)?.special_instructions || ""
              : "") ||
            "";

          // --- Main Form Data ---
          setFormData((prev) => ({
            ...prev,
            ...data,
            due_date: data.due_date ? data.due_date.split("T")[0] : "",
            documents_to_be_served: docsToBeServed,
            service_instructions: serviceInst,
            special_instructions: specialInst,
            custom_fields: data.custom_fields || {},
            assigned_to_ids: assignedIds,
            client_contact_person: data.client_info?.contact_person || "",
            client_phone_number: data.client_info?.phone_number || "",
            client_email_address: data.client_info?.email_address || "",
            client_billing_address: data.client_info?.billing_address || "",
            preferred_contact_method:
              data.client_info?.preferred_contact_method || "Email",
          }));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.response?.data?.message || "Failed to load case data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEditMode, PS_API_URL, token]);

  // --- Handlers ---
  const handleChange = useCallback(
    (e) => {
      const { name, value, type, options } = e.target;

      if (name === "assigned_to_ids" && type === "select-multiple") {
        const selectedIds = Array.from(options)
          .filter((option) => option.selected)
          .map((option) => parseInt(option.value, 10));

        setFormData((prev) => ({ ...prev, [name]: selectedIds }));
        return;
      }

      setFormData((prev) => {
        let newState = { ...prev, [name]: value };

        if (name === "client_id") {
          const selectedClient = clients.find(
            (c) => c.id === parseInt(value, 10),
          );

          if (selectedClient) {
            newState = {
              ...newState,
              client_contact_person:
                selectedClient.contact_person ||
                `${selectedClient.first_name} ${selectedClient.last_name}`,
              client_phone_number: selectedClient.phone_number || "",
              client_email_address: selectedClient.email || "",
              client_billing_address: selectedClient.billing_address || "",
              preferred_contact_method:
                selectedClient.preferred_contact_method || "Email",
            };

            if (runTour && stepIndex === 4) {
              newState.case_name = "Doe vs. Smith - Subpoena";
              newState.county_or_region = "Springfield";
              newState.state_or_province = "IL";
              newState.court_jurisdiction = "Circuit";
              newState.service_type = "Standard";
              newState.client_reference_number = "REF-TOUR-123";
              newState.due_date = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0];
            }
          } else if (value === "") {
            newState = {
              ...newState,
              client_contact_person: "",
              client_phone_number: "",
              client_email_address: "",
              client_billing_address: "",
              preferred_contact_method: "Email",
            };
          }
        }
        return newState;
      });
    },
    [clients, runTour, stepIndex],
  );

  const handleCustomFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [name]: value,
      },
    }));
  };

  const handleServeeChange = (index, e) => {
    const { name, value } = e.target;
    const newServees = [...serveeData];
    newServees[index][name] = value;
    setServeeData(newServees);
  };

  const handleRemoveServee = (indexToRemove) => {
    setServeeData((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleDocumentTypeChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const currentDocs = prev.documents_to_be_served || [];
      if (checked) {
        return { ...prev, documents_to_be_served: [...currentDocs, value] };
      } else {
        return {
          ...prev,
          documents_to_be_served: currentDocs.filter((doc) => doc !== value),
        };
      }
    });
  };

  const handleServeeAddFile = (index, fileObject) => {
    setServeeData((prev) =>
      prev.map((servee, i) =>
        i === index
          ? { ...servee, servee_files: [...servee.servee_files, fileObject] }
          : servee,
      ),
    );
  };

  const handleServeeRemoveFile = (serveeIndex, fileIndex, fileId) => {
    const fileToRemove = serveeData[serveeIndex].servee_files[fileIndex];
    if (fileId && !fileToRemove.isNew) {
      handleDeleteServeeFile(fileId);
    }
    setServeeData((prev) =>
      prev.map((servee, i) =>
        i === serveeIndex
          ? {
              ...servee,
              servee_files: servee.servee_files.filter(
                (_, j) => j !== fileIndex,
              ),
            }
          : servee,
      ),
    );
  };

  const handleFileUploadTemp = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCurrentFileUpload((prev) => ({
        ...prev,
        file: file,
        title: prev.title || file.name,
        isNew: true,
      }));
    }
  };

  const handleTitleChangeTemp = (e) => {
    setCurrentFileUpload((prev) => ({ ...prev, title: e.target.value }));
  };

  const handleAddFile = () => {
    if (currentFileUpload.file && currentFileUpload.title.trim() !== "") {
      setCaseFiles((prev) => [...prev, currentFileUpload]);
      setCurrentFileUpload({ file: null, title: "" });
      document.getElementById("case_file_upload").value = null;
    } else {
      alert("Please select a file and enter a title.");
    }
  };

  const handleRemoveNewFile = (indexToRemove) => {
    const newFiles = caseFiles.filter((f) => f.isNew);
    if (indexToRemove >= 0 && indexToRemove < newFiles.length) {
      const fileToRemove = newFiles[indexToRemove];
      setCaseFiles((prev) => {
        const globalIndex = prev.findIndex((f) => f === fileToRemove);
        if (globalIndex > -1) {
          return prev.filter((_, i) => i !== globalIndex);
        }
        return prev;
      });
    }
  };

  const handleRemoveExistingFile = (fileId, fileType) => {
    handleDeleteCaseFile(fileId, fileType);
  };

  // --- Submission ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus("submitting");
    setError(null);

    let tourCaseNumber = null;
    if (runTour) {
      tourCaseNumber = `DEMO-${Math.floor(Math.random() * 90000) + 10000}`;
    }

    const clientInfoData = {
      client_id: formData.client_id,
      contact_person: formData.client_contact_person,
      phone_number: formData.client_phone_number,
      email_address: formData.client_email_address,
      billing_address: formData.client_billing_address,
      preferred_contact_method: formData.preferred_contact_method,
    };

    const assignedToIdString = (formData.assigned_to_ids || []).join(",");

    const submissionData = {
      case_data: {
        ...formData,
        case_number: tourCaseNumber || formData.case_number,
        client_id: formData.client_id || null,
        assigned_to_ids: formData.assigned_to_ids || [],
        assigned_to_id: assignedToIdString || null,
        due_date: formData.due_date || null,
        is_pi: false,
        case_type: "Process Service",
        client_info: clientInfoData,
        documents_to_be_served: (formData.documents_to_be_served || []).join(","),
        service_instructions: formData.service_instructions || "",
        special_instructions: formData.special_instructions || "",
      },
      servee_data: serveeData.map(({ servee_files, ...rest }) => ({ ...rest })),
      custom_fields: formData.custom_fields,
    };

    let finalCaseId;
    let serveeCreationResponse = [];

    try {
      let response;
      if (isEditMode) {
        const updatePayload = {
          ...submissionData.case_data,
          assigned_to_ids: formData.assigned_to_ids || [],
          assigned_to_id: assignedToIdString || null,
          documents_to_be_served: (formData.documents_to_be_served || []).join(","),
          service_instructions: formData.service_instructions || "",
          special_instructions: formData.special_instructions || "",
          custom_fields: submissionData.custom_fields,
          servee_data: submissionData.servee_data,
        };
        response = await axios.put(
          `${PS_API_URL}/${id}`,
          updatePayload,
          authHeaders,
        );
        finalCaseId = id;
        serveeCreationResponse = response.data.servees || [];
      } else {
        response = await axios.post(PS_API_URL, submissionData, authHeaders);
        finalCaseId = response.data.caseId;
        serveeCreationResponse = response.data.servees || [];
      }

      const finalServeeData = serveeData
        .map((localServee, index) => {
          const matchedServee =
            serveeCreationResponse.find((s) => s.id === localServee.id) ||
            serveeCreationResponse[index];
          return {
            ...localServee,
            id: matchedServee?.id || localServee.id,
          };
        })
        .filter((s) => s.id);

      const uploadPromises = [];
      const uploadConfig = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      };

      caseFiles
        .filter((item) => item.isNew)
        .forEach((item) => {
          const fd = new FormData();
          fd.append("document", item.file);
          fd.append("document_type", item.title);
          fd.append(
            "documents_to_be_served",
            formData.documents_to_be_served.join(","),
          );
          fd.append("service_instructions", formData.service_instructions);
          fd.append("special_instructions", formData.special_instructions);
          uploadPromises.push(
            axios.post(`${PS_API_URL}/${finalCaseId}/upload`, fd, uploadConfig),
          );
        });

      finalServeeData.forEach((servee) => {
        servee.servee_files
          .filter((item) => item.isNew)
          .forEach((item) => {
            const fd = new FormData();
            fd.append("document", item.file);
            fd.append("document_type", item.title);
            fd.append("servee_id", servee.id);
            uploadPromises.push(
              axios.post(
                `${PS_API_URL}/${finalCaseId}/upload/servee-file`,
                fd,
                uploadConfig,
              ),
            );
          });
      });

      await Promise.all(uploadPromises);
      setIsSaved(true);
      setSubmitStatus("success");

      if (runTour && stepIndex === 4) {
        setStepIndex(5);
        navigate("/cases");
      } else {
        toast.success("Case saved successfully!");
        setTimeout(() => navigate("/cases"), 1500);
      }
    } catch (err) {
      setSubmitStatus("error");
      toast.error("Failed to save the case.");
      console.error("Submission Error:", err.response?.data || err);
      setError(
        err.response?.data?.message || "Submission failed. Please try again.",
      );
    }
  };

  if (loading) return <div className="p-6 text-center">Loading form...</div>;
  if (error && submitStatus !== "submitting")
    return <div className="p-6 text-center text-red-500">Error: {error}</div>;

  // Add this helper function right above your return statement
  const getClientLabel = (c) => {
    const first = c.first_name || "";
    const last = c.last_name === "NA" ? "" : c.last_name || "";
    const fullName = `${first} ${last}`.trim();

    if (c.agency) {
      return fullName ? `${c.agency} (${fullName})` : c.agency;
    }
    return fullName || "Unknown Client";
  };

  return (
    <div className="p-1 sm:p-4 lg:p-6 max-w-6xl mx-auto">
      <h1 className="text-4xl font-extrabold text-gray-800 mb-8">
        {id
          ? "Edit Process Service Case"
          : "IntelServe™ Digital Process Service Intake"}
      </h1>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* 1. Client Information */}
        <FormSection
          title="1. Client Information"
          icon={<BiUser className="w-6 h-6" />}
          className="tour-intake-client-select"
        >
          <div className="col-span-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search & Select Client / Firm *
            </label>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <div className="flex-1">
                <Select
                  options={clients.map((c) => ({
                    value: c.id,
                    label: c.agency
                      ? `${c.agency}${c.first_name && " ("} ${c.first_name} ${c.last_name == "NA" ? "" : c.last_name}${c.first_name && ")"}`
                      : `${c.first_name} ${c.last_name == "NA" ? "" : c.last_name}`,
                    client: c,
                  }))}
                  value={
                    formData.client_id
                      ? clients
                          .map((c) => ({
                            value: c.id,
                            label: c.agency
                              ? `${c.agency}${c.first_name && " ("} ${c.first_name} ${c.last_name == "NA" ? "" : c.last_name}${c.first_name && ")"}`
                              : `${c.first_name} ${c.last_name == "NA" ? "" : c.last_name}`,
                            client: c,
                          }))
                          .find((opt) => opt.value === formData.client_id)
                      : null
                  }
                  onChange={(selected) => {
                    if (!selected) {
                      setFormData((prev) => ({
                        ...prev,
                        client_id: "",
                        client_contact_person: "",
                        client_phone_number: "",
                        client_email_address: "",
                        client_billing_address: "",
                      }));
                      return;
                    }
                    const selectedClient = selected.client;

                    setFormData((prev) => ({
                      ...prev,
                      client_id: selected.value,
                      client_contact_person:
                        selectedClient.contact_person ||
                        `${selectedClient.first_name} ${selectedClient.last_name}`,
                      client_phone_number: selectedClient.phone_number || "",
                      client_email_address: selectedClient.email || "",
                      client_billing_address:
                        selectedClient.billing_address || "",
                      preferred_contact_method:
                        selectedClient.preferred_contact_method || "Email",
                    }));
                  }}
                  placeholder="Search client or agency..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  className="tour-select-client shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2"
                  isSearchable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                    menu: (base) => ({ ...base, zIndex: 99999 }),
                  }}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                {formData.client_id && (
                  <Link
                    to={`/clients/${formData.client_id}`}
                    className="w-full sm:w-auto text-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Edit
                  </Link>
                )}

                <span className="tour-add-client-btn w-full sm:w-auto">
                  <Link
                    to="/clients/new"
                    className="w-full sm:w-auto text-center px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 block"
                  >
                    Add New Client
                  </Link>
                </span>
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="client_contact_person"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Contact Person *
            </label>
            <input
              type="text"
              id="client_contact_person"
              name="client_contact_person"
              value={formData.client_contact_person}
              disabled={!formData.client_id}
              onChange={handleChange}
              required
              className="p-1.5 border rounded-md w-full"
            />
          </div>

          <div>
            <label
              htmlFor="client_phone_number"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Phone Number *
            </label>
            <input
              type="tel"
              id="client_phone_number"
              name="client_phone_number"
              value={formData.client_phone_number}
              disabled={!formData.client_id}
              onChange={handleChange}
              required
              className="p-1.5 border rounded-md w-full"
            />
          </div>

          <div>
            <label
              htmlFor="client_email_address"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email Address *
            </label>
            <input
              type="email"
              id="client_email_address"
              name="client_email_address"
              value={formData.client_email_address}
              disabled={!formData.client_id}
              onChange={handleChange}
              required
              className="p-1.5 border rounded-md w-full"
            />
          </div>

          <div>
            <label
              htmlFor="client_billing_address"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Billing Address
            </label>
            <input
              type="text"
              id="client_billing_address"
              name="client_billing_address"
              disabled={!formData.client_id}
              value={formData.client_billing_address}
              onChange={handleChange}
              className="p-1.5 border rounded-md w-full"
            />
          </div>

          <div>
            <label
              htmlFor="preferred_contact_method"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Preferred Contact Method *
            </label>
            <select
              id="preferred_contact_method"
              name="preferred_contact_method"
              value={formData.preferred_contact_method}
              disabled={!formData.client_id}
              onChange={handleChange}
              required
              className="p-1.5 border rounded-md bg-gray-50 w-full"
            >
              <option value="">Select...</option>
              {PREFERRED_CONTACT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </FormSection>

        {/* 2. Case Information */}
        <FormSection
          title="2. Case Information"
          icon={<BiBriefcase className="w-6 h-6" />}
          className="tour-intake-case-title"
        >
          <div>
            <label
              htmlFor="case_name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Case Title *
            </label>
            <input
              type="text"
              id="case_name"
              name="case_name"
              placeholder="e.g. Smith v. Johnson"
              value={formData.case_name}
              onChange={handleChange}
              required
              className="p-1.5 border rounded-md w-full"
            />
          </div>
          <div>
            <label
              htmlFor="case_number"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Case Number
            </label>
            <input
              type="text"
              id="case_number"
              name="case_number"
              value={formData.case_number}
              onChange={handleChange}
              className="p-1.5 border rounded-md w-full"
            />
          </div>
          <div>
            <label
              htmlFor="state_or_province"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              State / Province *
            </label>
            <input
              type="text"
              id="state_or_province"
              name="state_or_province"
              value={formData.state_or_province}
              onChange={handleChange}
              required
              className="p-1.5 border rounded-md w-full"
            />
          </div>
          <div>
            <label
              htmlFor="county_or_region"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              County / Region *
            </label>
            <input
              type="text"
              id="county_or_region"
              name="county_or_region"
              value={formData.county_or_region}
              onChange={handleChange}
              required
              className="p-1.5 border rounded-md w-full"
            />
          </div>

          <div>
            <label
              htmlFor="court_jurisdiction"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Court / Jurisdiction *
            </label>
            <select
              id="court_jurisdiction"
              name="court_jurisdiction"
              value={formData.court_jurisdiction}
              onChange={handleChange}
              required
              className="p-1.5 border rounded-md bg-gray-50 w-full"
            >
              <option value="">Select...</option>
              {COURT_JURISDICTIONS.map((j) => (
                <option key={j} value={j}>
                  {j}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="service_type"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Type of Service *
            </label>
            <select
              id="service_type"
              name="service_type"
              value={formData.service_type}
              onChange={handleChange}
              required
              className="p-1.5 border rounded-md bg-gray-50 w-full"
            >
              <option value="">Select...</option>
              {SERVICE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="client_reference_number"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Client Reference #
            </label>
            <input
              type="text"
              id="client_reference_number"
              name="client_reference_number"
              value={formData.client_reference_number}
              onChange={handleChange}
              className="p-1.5 border rounded-md w-full"
            />
          </div>
          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="p-1.5 border rounded-md bg-gray-50 w-full"
            >
              {caseStatuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="due_date"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Due Date
            </label>
            <input
              type="date"
              id="due_date"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
              className="p-1.5 border rounded-md w-full"
            />
          </div>
          <div className="col-span-full">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Internal Case Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="p-1.5 border rounded-md w-full"
            />
          </div>
        </FormSection>

        {/* 3. Servee Information */}
        <div className="border border-gray-200 rounded-xl p-6 bg-white space-y-4">
          <h2 className="text-xl font-extrabold text-blue-800 flex items-center space-x-2 border-b pb-2">
            <BiMap className="w-6 h-6" />
            <span>3. Servee Information</span>
          </h2>
          <div className="space-y-6">
            {serveeData.map((servee, index) => (
              <ServeeFormBlock
                key={servee.id || index}
                servee={servee}
                index={index}
                handleServeeChange={handleServeeChange}
                handleRemoveServee={handleRemoveServee}
                handleServeeAddFile={handleServeeAddFile}
                handleServeeRemoveFile={handleServeeRemoveFile}
                canRemove={serveeData.length > 1}
                isServeeNew={!servee.id}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              setServeeData((prev) => [...prev, newServeeTemplate])
            }
            className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg shadow-md hover:bg-green-600 transition duration-300"
          >
            <BiPlus className="w-5 h-5 mr-1" /> Add Another Servee
          </button>
        </div>

        {/* 4. Documents & Instructions (RESTORED) */}
        <div className="border border-gray-200 rounded-xl p-6 bg-white space-y-6">
          <h2 className="text-xl font-extrabold text-blue-800 flex items-center space-x-2 border-b pb-2">
            <BiFile className="w-6 h-6" />
            <span>4. Documents & Instructions</span>
          </h2>

          <div className="col-span-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Documents to be Served (Multi-select)
            </label>
            <div className="flex flex-wrap gap-3">
              {DOCUMENT_TYPES_TO_BE_SERVED.map((doc) => (
                <label
                  key={doc}
                  className="flex items-center space-x-1 border border-gray-300 px-3 py-1 rounded-full cursor-pointer hover:bg-gray-100 transition duration-150"
                >
                  <input
                    type="checkbox"
                    value={doc}
                    checked={formData.documents_to_be_served.includes(doc)}
                    onChange={handleDocumentTypeChange}
                    className="form-checkbox text-blue-600 rounded"
                  />
                  <span className="text-sm">{doc}</span>
                </label>
              ))}
            </div>
          </div>

          {isEditMode && (
            <ExistingCaseFiles
              files={caseFiles}
              handleRemoveExistingFile={handleRemoveExistingFile}
            />
          )}

          <div className="space-y-4 border border-blue-100 p-4 rounded-lg bg-blue-50">
            <h3 className="text-lg font-bold text-blue-700">
              Upload New Case Documents
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label
                  htmlFor="file_title"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Document Title *
                </label>
                <input
                  type="text"
                  id="file_title"
                  name="file_title"
                  placeholder="e.g., Complaint and Summons"
                  value={currentFileUpload.title}
                  onChange={handleTitleChangeTemp}
                  required={currentFileUpload.file !== null}
                  className="p-1.5 border rounded-md w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File Upload *
                </label>
                <input
                  type="file"
                  id="case_file_upload"
                  onChange={handleFileUploadTemp}
                  accept={ALLOWED_SERVE_FILE_TYPES}
                  className="block w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 cursor-pointer p-1.5"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleAddFile}
              disabled={
                !currentFileUpload.file || currentFileUpload.title.trim() === ""
              }
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition duration-300 disabled:opacity-50"
            >
              <BiPlus className="w-5 h-5 mr-1" /> Add File to Case
            </button>
          </div>

          {caseFiles.filter((f) => f.isNew).length > 0 && (
            <div className="col-span-full">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                New Files to be Uploaded (
                {caseFiles.filter((f) => f.isNew).length}):
              </label>
              <ul className="text-sm text-gray-700 space-y-2">
                {caseFiles
                  .filter((f) => f.isNew)
                  .map((item, index) => (
                    <li
                      key={index}
                      className="flex justify-between items-center bg-gray-50 p-2 rounded-md border border-gray-200"
                    >
                      <div className="truncate pr-4">
                        <span className="font-semibold">{item.title}</span>
                        <span className="text-gray-500 ml-2">
                          ({item.file.name})
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          ({(item.file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveNewFile(index)}
                        className="text-red-500 hover:text-red-700 flex-shrink-0"
                      >
                        <BiTrash className="w-5 h-5" />
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          <div className="col-span-full md:col-span-1">
            <label
              htmlFor="number_of_pages"
              className="block text-sm font-medium text-gray-700"
            >
              Number of Pages
            </label>
            <input
              type="number"
              id="number_of_pages"
              name="number_of_pages"
              value={formData.number_of_pages}
              onChange={handleChange}
              min="0"
              className="p-1.5 border rounded-md w-full"
            />
          </div>
          <div className="col-span-full">
            <label
              htmlFor="service_instructions"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Service Instructions
            </label>
            <textarea
              id="service_instructions"
              name="service_instructions"
              placeholder="'Personal only', 'Mail after attempts', etc."
              value={formData.service_instructions}
              onChange={handleChange}
              rows="3"
              className="p-1.5 border rounded-md w-full"
            />
          </div>
          <div className="col-span-full">
            <label
              htmlFor="special_instructions"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Special Instructions
            </label>
            <textarea
              id="special_instructions"
              name="special_instructions"
              placeholder="Gate code, timing, hazards, etc."
              value={formData.special_instructions}
              onChange={handleChange}
              rows="3"
              className="p-1.5 border rounded-md w-full"
            />
          </div>
        </div>

        {/* 5. Internal Assignments & Custom Fields (RESTORED) */}
        <FormSection
          title="5. Internal Assignments"
          icon={<BiUser className="w-6 h-6" />}
        >
          <div>
            <label
              htmlFor="assigned_to_ids"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Assigned Agent(s) (Hold Ctrl/Cmd to select multiple)
            </label>
            <select
              id="assigned_to_ids"
              name="assigned_to_ids"
              multiple
              value={formData.assigned_to_ids.map(String)}
              onChange={handleChange}
              className="p-1.5 border rounded-md bg-gray-50 w-full h-40"
            >
              <option value="">Unassigned</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} (
                  {formatRoleName(user.role_name)})
                </option>
              ))}
            </select>
          </div>
        </FormSection>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={() => navigate("/cases")}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-300"
          >
            <BiX className="inline-block w-5 h-5 mr-1" /> Cancel
          </button>

          <span className="tour-intake-submit-btn inline-block">
            <button
              type="submit"
              disabled={submitStatus === "submitting"}
              className="px-8 py-3 font-bold text-white bg-blue-600 rounded-lg shadow-lg hover:bg-blue-700 transition duration-300 disabled:opacity-50 flex items-center justify-center tour-create-intake-btn"
            >
              {submitStatus === "submitting" ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving Case...
                </>
              ) : isSaved ? (
                <>
                  <BiSave className="inline-block w-5 h-5 mr-1" /> Saved
                </>
              ) : (
                <>
                  <BiSave className="inline-block w-5 h-5 mr-1" />{" "}
                  {id ? "Update Case" : "Create New Intake"}
                </>
              )}
            </button>
          </span>
        </div>

        {submitStatus === "success" && (
          <p className="mt-4 text-green-500 text-center font-semibold">
            Case saved successfully!{" "}
          </p>
        )}
        {submitStatus === "error" && (
          <p className="mt-4 text-red-500 text-center font-semibold">{error}</p>
        )}
      </form>
    </div>
  );
};

export default CaseForm;
